<script setup>
import { computed, ref } from 'vue';
import { t } from '../i18n';
import {
  atStartLevels, daysLabel, fastest, formatDay, formatLevel, formatRub, progress, untilText,
} from '../achievements';

// A whole ladder in the sheet of «Достижения» (docs/achievements/SPEC.md, «Проект v2»): the sum now and the way
// to the next level, then every level reached, newest first; small first levels fold into «Первые шаги», levels
// there on the start into one grey row; the quickest one is marked.
const props = defineProps({
  track: {
    type: Object,
    required: true,
  },
  // Levels below this fold into «Первые шаги» (from a million of capital); 0 — none fold
  foldBelow: {
    type: Number,
    default: 0,
  },
});

const reachedRows = computed(() => props.track.levels.filter((l) => !l.at_start).reverse());
const rows = computed(() => reachedRows.value.filter((l) => l.level >= props.foldBelow));
const firstSteps = computed(() => reachedRows.value.filter((l) => l.level < props.foldBelow).reverse());
const firstOpen = ref(false);
const atStart = computed(() => atStartLevels(props.track));
const quickest = computed(() => fastest(props.track));
const percent = computed(() => Math.round(progress(props.track) * 100));
</script>

<template>
  <div>
    <p
      class="text-2xl font-semibold tabular-nums text-slate-100"
    >
      {{ formatRub(track.current) }}
    </p>
    <template v-if="track.next">
      <div
        class="mt-3 h-2 overflow-hidden rounded-full bg-slate-800"
        aria-hidden="true"
      >
        <div
          class="h-full rounded-full bg-[#3987e5]"
          :style="{ width: `${percent}%` }"
        />
      </div>
      <p class="mt-2 text-[0.8125rem] text-slate-400">
        {{ untilText(track) }} · {{ t('achievements.onTheWayShort', { days: daysLabel(track.next.days) }) }}
      </p>
    </template>

    <p
      v-if="reachedRows.length === 0 && atStart.length === 0"
      class="mt-4 text-[0.9375rem] text-slate-400 sm:text-sm"
    >
      {{ t('achievements.firstLevel', { level: formatLevel(track.next?.level ?? 0) }) }}
    </p>
    <ul class="mt-3 divide-y divide-slate-800">
      <li
        v-for="level in rows"
        :key="level.level"
        class="flex min-h-12 items-center justify-between gap-3 py-2"
      >
        <span class="flex min-w-0 items-center gap-2">
          <span class="text-[0.9375rem] tabular-nums text-slate-100 sm:text-sm">{{ formatLevel(level.level) }}</span>
        </span>
        <span class="shrink-0 text-right leading-tight">
          <span class="block text-[0.8125rem] text-slate-300">{{ formatDay(level.date) }}</span>
          <span class="block text-[0.8125rem] text-slate-400">
            <span
              v-if="level.level === quickest"
              class="mr-1 text-[0.6875rem] text-emerald-400"
            >{{ t('achievements.fastest') }}</span>
            {{ t('achievements.in', { days: daysLabel(level.days) }) }}
          </span>
        </span>
      </li>
      <li v-if="firstSteps.length > 0">
        <button
          type="button"
          class="flex min-h-12 w-full items-center justify-between gap-3 py-2 text-left text-[0.8125rem] text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
          :aria-expanded="firstOpen"
          @click="firstOpen = !firstOpen"
        >
          <span>{{ t('achievements.firstSteps', { from: formatLevel(firstSteps[0].level), to: formatLevel(firstSteps.at(-1).level), count: firstSteps.length }) }}</span>
          <span
            class="text-lg leading-none text-slate-500 transition-transform"
            :class="firstOpen ? 'rotate-90' : ''"
            aria-hidden="true"
          >›</span>
        </button>
        <ul
          v-if="firstOpen"
          class="divide-y divide-slate-800 pl-3"
        >
          <li
            v-for="level in [...firstSteps].reverse()"
            :key="level.level"
            class="flex min-h-11 items-center justify-between gap-3 py-1.5 text-[0.8125rem]"
          >
            <span class="tabular-nums text-slate-300">{{ formatLevel(level.level) }}</span>
            <span class="text-slate-500">{{ formatDay(level.date) }} · {{ t('achievements.in', { days: daysLabel(level.days) }) }}</span>
          </li>
        </ul>
      </li>
      <li
        v-if="atStart.length > 0"
        class="flex min-h-12 items-center py-2 text-[0.8125rem] text-slate-500"
      >
        {{ t('achievements.atStartRow', { from: formatLevel(atStart[0].level), to: formatLevel(atStart.at(-1).level), count: atStart.length }) }}
      </li>
    </ul>
  </div>
</template>
